import {
  bagsSellBagSsrs,
  bagsSellBagSsrsCheckInSuccess,
  bagsSellBagSsrsSuccess,
  bagsSellBagSsrsWithoutStepCompletion,
  bagStoreBagUpdateRequest,
  bagsValidateBagSelection,
} from '@actions/bags.action';
import { Injectable, Type } from '@angular/core';
import { Router } from '@angular/router';
import { LeftImageModalComponent } from '@components/modals/left-image-modal/left-image-modal.component';
import {
  FlowBaseUrlEnum,
  MODAL_DIALOG_CENTER_LG,
  MODAL_DIALOG_LIMITED_WIDTH, MODAL_DIALOG_STANDARD_WIDTH,
} from '@constants/app.constants';
import { CabinProductClass } from '@constants/cabin.constants';
import { ModalModeEnum } from '@constants/modals.constants';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { selectBagsTotalSelectionsState } from '@selectors/bags.selector';
import {
  selectJourneyBagsCanSell,
  selectJourneyDetails,
} from '@selectors/booking.selector';
import { selectBagsCopy } from '@selectors/cms.selector';
import { selectIsModifyPath } from '@selectors/router.selector';
import { SsrService } from '@services/api-clients/ssr.service';
import { ModalService } from '@services/modal/modal.service';
import { getRootPath } from '@utils/data-utils';
import { Observable, of } from 'rxjs';
import { map, mergeMap, switchMap, take } from 'rxjs/operators';

import { appNoOp } from '../actions/app.action';
import { NoBagsModalComponent } from '@components/modals/no-bags-modal/no-bags-modal.component';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { BsModalRef } from 'ngx-bootstrap/modal';
import {StandardBagSsrs} from "@constants/bags.constants";
import {
  StandardConfirmationMessageModalComponent
} from "@components/modals/standard-confirmation-message-modal/standard-confirmation-message-modal.component";
import {updatePassengerSeatsSuccess} from "@actions/seat.action";

@Injectable()
export class BagEffect {
  get rootPath(): FlowBaseUrlEnum {
    return getRootPath(this.router.url);
  }
  readonly gbf = GrowthBookFeatureKeys;

  validateBagSelection$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bagsValidateBagSelection),
      concatLatestFrom(() => [
        this.store.select(selectJourneyBagsCanSell),
        this.store.select(selectBagsTotalSelectionsState),
        this.store.select(selectBagsCopy),
        this.store.select(selectIsModifyPath),
        this.store.select(selectJourneyDetails),
      ]),
      switchMap(
        ([action, journeys, selections, bagsCopy, isModifyPath, journeyDetails]) => {
          const shouldDisplayModal = (j) => {
            const journeyDetail = journeyDetails.find((d) => d.journeyKey === j.journeyKey);
            const selection = selections?.find((s) => s.journeyKey === j.journeyKey);

            const passengersHaveNoBags = selection?.passengers.every((p) =>
              p.ssrs
                .filter(
                  (ssr) =>
                    ssr.ssrCode !== StandardBagSsrs.CARRYON_BAG &&
                    ssr.ssrCode !== StandardBagSsrs.CARRYON_BAG_15,
                )
                .every((ssr) => ssr.count === 0),
            ) ?? selections === null;

            return (
              j.productClass === CabinProductClass.ECONOMY_LIGHT &&
              journeyDetail?.canSellBags === true &&
              (journeyDetail.restrictBagsToCheckin ? !isModifyPath : true) &&
              passengersHaveNoBags
            );
          };

          const displayModal = this.gbs.isVariantOfExperimentEnabled('feat-003125-updated-ancillary-pop-up-logic', 'b')
            ? journeys?.every(shouldDisplayModal)
            : journeys?.some(shouldDisplayModal);

          if (displayModal && bagsCopy) {
            let componentToLoad: Type<
              LeftImageModalComponent | NoBagsModalComponent
            > = LeftImageModalComponent;
            let classToLoad: string = MODAL_DIALOG_CENTER_LG;
            const newNoBagsModalInitialState = {
              modalMode: ModalModeEnum.UPGRADE,
            };
            let initialState: object = {
              btnPrimaryText: bagsCopy.travelingLightAdd,
              btnCancelText: bagsCopy.travelingLightNoThanks,
              headerText: bagsCopy.travelingLightHeaderText,
              subText: bagsCopy.travelingLightText,
              img: bagsCopy.travelingLightImage?.url ?? '',
              altText: bagsCopy.travelingLightImage?.altText ?? '',
              modalMode: ModalModeEnum.UPGRADE,
            };
            if (
              this.gbs.isOn(this.gbf.FEAT_US179_NO_BAGS_ARE_SELECTED) &&
              this.gbs.getFeatureStringValue(
                this.gbf.FEAT_US179_NO_BAGS_ARE_SELECTED,
              ) !== 'a'
            ) {
              componentToLoad = NoBagsModalComponent;
              initialState = newNoBagsModalInitialState;
              classToLoad = MODAL_DIALOG_LIMITED_WIDTH;
            }

            if (this.gbs.isVariantOfExperimentEnabled('feat-001562-concise-pop-ups-seats-meals-bags-connections-etc', 'b')) {
              const modalRef = this.modalService.show(StandardConfirmationMessageModalComponent, {
                class: MODAL_DIALOG_STANDARD_WIDTH,
                initialState: {
                  primaryBtnText: bagsCopy.travelingLightAdd,
                  secondaryBtnText: bagsCopy.travelingLightNoThanks,
                  titleText: bagsCopy.travelingLightHeaderText,
                  messageText: bagsCopy.travelingLightText,
                  imageUrl: bagsCopy.travelingLightImage?.url ?? '',
                  variant: 'b'
                },
              });

              if (!modalRef.content) {
                console.error('bags modal could not be displayed');
                return of(appNoOp());
              }
              return modalRef.content.response.pipe(
                take(1),
                switchMap((e) => {
                  if (e !== 'primaryClick') {
                    return of(bagsSellBagSsrs({redirect: action.redirect, redirectToPayment: action.redirectToPayment}));
                  }
                  return of(appNoOp());
                }),
              );

            } else {

            const modalRef: BsModalRef<
              LeftImageModalComponent | NoBagsModalComponent
            > = this.modalService.show(componentToLoad, {
              class: classToLoad,
              initialState: initialState,
            });

            if (!modalRef.content) {
              console.error('bags modal could not be displayed');
              return of(appNoOp());
            }
              return modalRef.content.response.pipe(
                take(1),
                switchMap((accepted) => {
                  if (accepted) {
                    return of(bagsSellBagSsrs({redirect: action.redirect, redirectToPayment: action.redirectToPayment}));
                  }
                  return of(appNoOp());
                }),
              );
            }

          } else {
            return of(bagsSellBagSsrs({redirect: action.redirect, redirectToPayment: action.redirectToPayment}));
          }
        },
      ),
    );
  });

  sellBagSsrs$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bagsSellBagSsrs),
      concatLatestFrom(() => this.store.select(selectBagsTotalSelectionsState)),
      mergeMap(([action, selection]) => {
        if (selection) {
          return this.ssrService
            .ssrUpdate(selection)
            .pipe(
              map(() =>
                this.rootPath === FlowBaseUrlEnum.CHECK_IN
                  ? bagsSellBagSsrsCheckInSuccess()
                  : bagsSellBagSsrsSuccess({redirect: action.redirect, redirectToPayment: action.redirectToPayment}),
              ),
            );
        } else {
          return of(appNoOp());
        }
      }),
    );
  });

  sellBagSsrsWithoutStepCompletion$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bagsSellBagSsrsWithoutStepCompletion),
      mergeMap((action) => {
        if (action.payload) {
          return this.ssrService
            .ssrUpdate(action.payload)
            .pipe(
              map(() =>
                action.next ? action.next : appNoOp()
              ),
            );
        } else {
          return of(appNoOp());
        }
      }),
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store,
    private ssrService: SsrService,
    private modalService: ModalService,
    private router: Router,
    public gbs: GrowthBookService,
  ) {}
}
